/**
 * @file 教师相关 API（对齐「思犇优学」`/pc/teacher` 文档）
 * @description 教师列表 / 增删改 / 启禁 / 重置密码。
 *
 * @whenToUse 教师管理页数据源。
 * @whenNotToUse 校区 / 学员接口（各自独立 api 文件）。
 */

import { del, get, getPage, post, put } from '@/lib/client.ts'

/** 教师列表行（分页） */
export type TeacherItem = {
  /** 教师 ID（后端 int64 雪花 id，JSON 序列化为字符串） */
  id: string
  /** 教师名称 */
  teacherName: string
  /** 教师账号 */
  userName: string
  /** 教师用户 id */
  userId: string
  /** 状态（0-禁用 1-正常） */
  status: string
  /** 授课科目（逗号拼接） */
  subjects: string
  /** 学员人数 */
  studentCount: number
  /** 创建时间 */
  createTime: string
}

/** 教师列表查询参数 */
export type TeacherListParams = {
  /** 页码，从 1 开始 */
  pageNum?: number
  /** 每页条数 */
  pageSize?: number
  /** 教师名称关键字 */
  teacherName?: string
  /** 教师账号关键字 */
  userName?: string
  /** 状态（0-禁用 1-正常） */
  status?: string
}

/** 新增教师请求体 */
export type TeacherCreatePayload = {
  /** 教师名称 */
  teacherName: string
  /** 教师账号 */
  userName: string
  /** 授课科目列表 */
  subjects: string[]
  /** 登录密码；为空则后端默认 123456 */
  password?: string
}

/** 编辑教师请求体（账号与密码不可改） */
export type TeacherEditPayload = {
  /** 教师 ID */
  id: string
  /** 教师名称 */
  teacherName: string
  /** 授课科目列表 */
  subjects: string[]
}

/**
 * 分页查询教师列表。
 * @param params 分页与筛选（teacherName / userName / status）
 */
export function fetchTeacherList(params?: TeacherListParams) {
  return getPage<TeacherItem>('/pc/teacher/list', { params })
}

/**
 * 教师详情。
 * @param id 教师 ID
 */
export function getTeacher(id: string) {
  return get<TeacherItem>(`/pc/teacher/${id}`)
}

/**
 * 新增教师。
 * @param payload 教师字段
 */
export function createTeacher(payload: TeacherCreatePayload) {
  return post<null>('/pc/teacher', payload)
}

/**
 * 编辑教师。
 * @param payload 教师字段（含 id）
 */
export function updateTeacher(payload: TeacherEditPayload) {
  return put<null>('/pc/teacher', payload)
}

/**
 * 删除教师（支持批量，逗号分隔）。
 * @param ids 教师 ID 串
 */
export function deleteTeacher(ids: string) {
  return del<null>(`/pc/teacher/${ids}`)
}

/**
 * 按学科查询教师下拉（不传 subject 返回全部）。
 * @param params 过滤条件
 */
export function fetchTeacherDdl(params?: { subjects?: string }) {
  return get<TeacherItem[]>('/pc/teacher/ddl', { params })
}

/**
 * 启用 / 禁用教师。
 * @param id 教师 ID
 * @param status 目标状态（0-禁用 1-正常）
 */
export function changeTeacherStatus(id: string, status: '0' | '1') {
  return put<null>('/pc/teacher/changeStatus', { id, status })
}

/**
 * 重置教师账号密码（重置为 123456）。
 * @param id 教师 ID
 */
export function resetTeacherPwd(id: string) {
  return post<null>('/pc/teacher/resetPwd', { id })
}
